import { CloseRounded, InsertDriveFileOutlined, UploadFileRounded } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Dialog, IconButton, Typography } from '@mui/material'
import { ChangeEvent, useState } from 'react'
import { useFeedback } from '../../../../providers/feedback/feeedback';
import { black, greyEnabled, purple, purpleLight } from '../../../../theme/colors';

interface Props {
    openPopup: boolean;
    setOpenPopup: (val: boolean) => void;
    uploadFile: (val: File) => void;
    currentTab: number;
    notificationSentMode: string;
    setNotificationSentMode: (val: string) => void;
    submitting?: boolean;
}

export default function UploadCsvFileForNotification({ openPopup,
    setOpenPopup,
    uploadFile,
    notificationSentMode,
    setNotificationSentMode,
    submitting = false }: Props) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const { showSnackbar } = useFeedback()
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const maxFileSize = 10 * 1024 * 1024;
        if (file != null) {
            if (file.size > maxFileSize) {
                showSnackbar("File size exceeds 10 MB");
            } else {
                setSelectedFile(file)
            }
        }
    };

    const handleClose = () => !submitting && setOpenPopup(false);

    return (
        <Dialog open={openPopup} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
            <Box sx={{ width: 460, maxWidth: "90vw" }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
                >
                    <Typography variant="h6" fontWeight={700} color={black}>
                        Upload .xlsx File
                    </Typography>
                    <IconButton onClick={handleClose} size="small" disabled={submitting}>
                        <CloseRounded sx={{ color: greyEnabled }} />
                    </IconButton>
                </Box>

                <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            border: '1px dashed #ABA0AB',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 2,
                            py: 3,
                            bgcolor: selectedFile ? purpleLight : "transparent",
                        }}
                    >
                        {!selectedFile ? <>
                            <UploadFileRounded sx={{ color: purple, fontSize: 32 }} />
                            <label htmlFor="fileUploadNotification">
                                <Button
                                    component="span"
                                    sx={{ color: purple, textTransform: "none", fontWeight: 600 }}
                                >
                                    Add from files
                                </Button>
                            </label>
                            <input
                                id="fileUploadNotification"
                                onChange={handleFileChange}
                                type="file"
                                style={{ display: 'none' }}
                                accept=".xlsx"
                            />
                        </> : (
                            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                <InsertDriveFileOutlined sx={{ color: purple }} />
                                <Typography fontWeight={600} color={black}>{selectedFile?.name}</Typography>
                                <IconButton size="small" onClick={() => setSelectedFile(null)} disabled={submitting}>
                                    <CloseRounded sx={{ fontSize: 18, color: greyEnabled }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    <Typography variant="body2" color={greyEnabled} mt={2}>
                        File format must be .xlsx and the total size of the uploaded document should not exceed 10MB.
                    </Typography>

                    <Typography variant="body1" fontWeight={600} color={black} mt={3} mb={1}>
                        Notification Option
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Chip
                            label="SMS"
                            clickable
                            disabled={submitting}
                            variant={notificationSentMode === 'SMS' ? "filled" : "outlined"}
                            color="primary"
                            onClick={() => setNotificationSentMode('SMS')}
                        />
                        <Chip
                            label="Whatsapp"
                            clickable
                            disabled={submitting}
                            variant={notificationSentMode === 'Whatsapp' ? "filled" : "outlined"}
                            color="primary"
                            onClick={() => setNotificationSentMode('Whatsapp')}
                        />
                    </Box>

                    <Box display="flex" gap={1.5} mt={4}>
                        <Button
                            fullWidth
                            disabled={submitting}
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                borderColor: purple,
                                color: purple,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": { borderColor: purple, bgcolor: purpleLight },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            disabled={!selectedFile || !notificationSentMode || submitting}
                            onClick={() => uploadFile(selectedFile!)}
                            variant="contained"
                            disableElevation
                            startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                            sx={{
                                bgcolor: purple,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": { bgcolor: "#7A2074" },
                            }}
                        >
                            Upload File
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    )
}
